import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Image as ImageIcon, X as XIcon, Check } from "lucide-react";

// Scene photos uploader — BTS / set / promo shots, up to 30.
// Two sources: (a) upload new via Cloudinary, OR (b) pick from existing Pictures gallery.
// When picked from gallery, the picture's `linked_video_id` will be set elsewhere.
export default function ScenePhotos({ value, onChange, max = 30, videoId = null }) {
  const [uploading, setUploading] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [...(value || [])];
    setUploading(files.length);
    let done = 0;
    for (const f of files) {
      if (next.length >= max) { toast.error(`Max ${max} scene photos`); break; }
      if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name} > 50MB — skipped`); done++; setUploading(files.length - done); continue; }
      try {
        const form = new FormData();
        form.append("file", f);
        form.append("folder", "videos/scenes");
        form.append("resource_type", "auto");
        const r = await api.post("/api/cloudinary/upload", form);
        next.push(r.data.secure_url);
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
      }
      done++;
      setUploading(files.length - done);
    }
    onChange(next);
    setUploading(0);
    e.target.value = "";
  };
  const removeAt = (i) => onChange(value.filter((_, j) => j !== i));
  const onGalleryPicked = async (pickedPics) => {
    setGalleryOpen(false);
    if (!pickedPics?.length) return;
    const next = [...(value || [])];
    const linked_ids = [];
    for (const p of pickedPics) {
      if (next.length >= max) { toast.error(`Max ${max} scene photos`); break; }
      next.push(p.image_url);
      linked_ids.push(p.id);
    }
    onChange(next);
    // If we know which video we're editing, write linked_video_id on each picked picture
    if (videoId && linked_ids.length > 0) {
      try {
        await Promise.all(linked_ids.map((pid) =>
          api.put(`/api/pictures/${pid}`, { linked_video_id: videoId })
        ));
        toast.success(`${linked_ids.length} picture${linked_ids.length === 1 ? "" : "s"} linked to this video`);
      } catch { /* non-fatal */ }
    }
  };
  return (
    <div className="rounded-lg border p-4 space-y-2" data-testid="vadd-scene-photos"
      style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
      <div className="overline flex items-center justify-between flex-wrap gap-2">
        <span>📸 SCENE PHOTOS · {value.length}/{max}</span>
        <div className="flex items-center gap-3">
          {uploading > 0 && <span className="text-[color:var(--gio-primary)] normal-case">uploading {uploading}…</span>}
          <button type="button" onClick={() => setGalleryOpen(true)} data-testid="vadd-scene-photos-pick-gallery"
            className="text-[10px] uppercase font-bold tracking-wider hover:text-[color:var(--gio-primary)] inline-flex items-center gap-1">
            <ImageIcon size={11} /> Pick from gallery
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
        {value.map((src, i) => (
          <div key={i} className="relative group rounded overflow-hidden border" style={{ borderColor: "var(--gio-border)" }}
            data-testid={`vadd-scene-photo-${i}`}>
            <img src={src} alt="" className="w-full h-20 object-cover" loading="lazy" />
            <button type="button" onClick={() => removeAt(i)} data-testid={`vadd-scene-photo-remove-${i}`}
              className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"
              style={{ background: "rgba(220,38,38,0.85)", color: "#fff" }}>×</button>
          </div>
        ))}
        {value.length < max && (
          <label className="flex flex-col items-center justify-center h-20 rounded border-2 border-dashed cursor-pointer hover:bg-[var(--gio-primary-soft)]"
            data-testid="vadd-scene-photos-upload"
            style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>
            <Plus size={16} />
            <span className="text-[10px] font-bold uppercase mt-1">Upload</span>
            <input type="file" accept="image/*,.heic,.heif" multiple onChange={onFile} className="hidden" />
          </label>
        )}
      </div>
      <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>BTS, screenshots, set, promo — upload new or pick from your Pictures gallery (auto-links to this video).</div>
      <PicturesGalleryPicker open={galleryOpen} onClose={() => setGalleryOpen(false)} onPick={onGalleryPicked} />
    </div>
  );
}

/* ── Pictures Gallery picker — pulls from /api/pictures/ ── */
function PicturesGalleryPicker({ open, onClose, onPick }) {
  const [pics, setPics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setQ("");
    setLoading(true);
    api.get("/api/pictures/").then(r => setPics(r.data || []))
      .catch(() => toast.error("Could not load pictures"))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;
  const filtered = pics.filter(p => {
    if (!q.trim()) return true;
    const term = q.toLowerCase();
    return (p.title || "").toLowerCase().includes(term)
      || (p.tags || []).some(t => (t || "").toLowerCase().includes(term))
      || (p.description || "").toLowerCase().includes(term);
  });
  const toggle = (p) => {
    setSelected(prev => prev.some(s => s.id === p.id) ? prev.filter(s => s.id !== p.id) : [...prev, p]);
  };
  const confirm = () => {
    if (selected.length === 0) return toast.error("Pick at least one");
    onPick(selected);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="pic-gallery-picker"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="bg-zinc-950 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} style={{ border: "1px solid var(--gio-border)" }}>
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <h3 className="font-heading text-lg">Pick from Pictures gallery</h3>
          <button onClick={onClose} className="hover:text-red-500"><XIcon size={18} /></button>
        </div>
        <div className="p-3 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="pic-gallery-search"
            placeholder="Search title / tags / description…" className="gio-input w-full" />
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="text-center py-12 text-[13px]" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-[13px]" style={{ color: "var(--gio-muted-3)" }}>
              No pictures match. Add some in /pictures first.
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {filtered.map(p => {
              const isSel = selected.some(s => s.id === p.id);
              return (
                <button key={p.id} onClick={() => toggle(p)} data-testid={`pic-gallery-item-${p.id}`}
                  className="relative aspect-square rounded-lg overflow-hidden border-2 transition hover:scale-105"
                  style={{ borderColor: isSel ? "var(--gio-primary)" : "var(--gio-border)" }}>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-900" style={{ color: "var(--gio-muted-3)" }}>
                      <ImageIcon size={20} />
                    </div>
                  )}
                  {isSel && (
                    <div className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ background: "var(--gio-primary)", color: "#000" }}>
                      <Check size={14} strokeWidth={3} />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                    <div className="text-[10px] text-white truncate">{p.title || "Untitled"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-3 border-t flex items-center justify-between" style={{ borderColor: "var(--gio-border)" }}>
          <span className="text-[12px]" style={{ color: "var(--gio-muted-2)" }}>{selected.length} selected</span>
          <button onClick={confirm} data-testid="pic-gallery-confirm" className="btn-primary text-[12px]">
            Add {selected.length || ""}
          </button>
        </div>
      </div>
    </div>
  );
}
